import { io, Socket } from 'socket.io-client';
import { env } from '@/core/config/env';
import { getOrCreateClientId } from '@/core/identity';

const isDev = import.meta.env.DEV;

let socket: Socket | null = null;
let isAuthenticated = false;
let shouldAuthenticate = false;
let refreshAttempted = false;

export interface RealtimeEvent {
  type: string;
  userId?: string;
  clientId?: string;
  payload: any;
  timestamp: number;
}

/**
 * Check if socket is currently authenticated
 */
export function isSocketAuthenticated(): boolean {
  return isAuthenticated;
}

/**
 * Try to refresh the access token and re-authenticate the socket.
 * Uses raw fetch to avoid circular dependency with apiClient.
 */
async function tryRefreshAndReauth(sock: Socket): Promise<void> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return;

  try {
    const response = await fetch(`${env.apiUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      const newToken = data.data?.accessToken;
      if (newToken) {
        localStorage.setItem('accessToken', newToken);
        if (isDev) console.log('[Socket] Token refreshed, re-authenticating socket');
        sock.emit('authenticate', { token: newToken });
      }
    }
  } catch (err) {
    if (isDev) console.warn('[Socket] Token refresh for socket failed:', err);
  }
}

/**
 * Create socket connection with optional token.
 * When token is provided (from authenticateSocket), it's sent in handshake and on connect.
 * When no token (from initializeSocket), socket starts anonymous and waits for
 * SessionContext to authenticate after token validation/refresh.
 */
function createSocket(token?: string): Socket {
  const clientId = getOrCreateClientId();
  const baseUrl = env.socketUrl;

  if (isDev) console.log('[Socket] Creating socket connection', { hasToken: !!token, clientId, baseUrl });

  const newSocket = io(baseUrl, {
    auth: {
      token: token || undefined,
      clientId,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
    withCredentials: true,
  });

  newSocket.on('connect', () => {
    if (isDev) console.log('[Socket] WebSocket connected', { socketId: newSocket.id });

    // Only re-authenticate if:
    // - Socket was created with a fresh token (authenticateSocket flow)
    // - Or socket was previously authenticated (reconnection after disconnect)
    // This prevents sending stale tokens from localStorage on initial load;
    // SessionContext handles initial auth after /session/init validates the token.
    if (token || shouldAuthenticate) {
      const currentToken = localStorage.getItem('accessToken') || token;
      if (currentToken) {
        if (isDev) console.log('[Socket] Re-authenticating socket on connect');
        newSocket.emit('authenticate', { token: currentToken });
      }
    }
  });

  newSocket.on('disconnect', (reason) => {
    if (isDev) console.log('[Socket] WebSocket disconnected', { reason });
    isAuthenticated = false;
  });

  newSocket.on('connect_error', (error) => {
    console.error('[Socket] WebSocket connection error:', error);
  });

  newSocket.on('authenticated', (data: { userId: string }) => {
    if (isDev) console.log('[Socket] Socket authenticated:', data.userId);
    isAuthenticated = true;
    shouldAuthenticate = true;
    refreshAttempted = false;
  });

  newSocket.on('authentication_error', async (error: { message: string }) => {
    if (isDev) console.warn('[Socket] Socket authentication error:', error.message);
    isAuthenticated = false;

    if (error.message === 'jwt expired' && !refreshAttempted) {
      refreshAttempted = true;
      await tryRefreshAndReauth(newSocket);
    }
  });

  newSocket.on('event', (event: RealtimeEvent) => {
    handleRealtimeEvent(event);
  });

  return newSocket;
}

/**
 * Initialize or get existing socket
 * 
 * IMPORTANT: This function is idempotent - calling it multiple times
 * will return the same socket instance unless it was explicitly disconnected.
 * 
 * Socket states:
 * - null: Never created → create new
 * - exists: Return existing (Socket.IO handles connect/reconnect automatically)
 */
export function initializeSocket(): Socket {
  if (socket) {
    if (isDev) {
      console.log('[Socket] initializeSocket - socket exists', {
        connected: socket.connected,
        disconnected: socket.disconnected,
        id: socket.id,
      });
    }
    return socket;
  }

  // Always start anonymous — SessionContext will authenticate after
  // /session/init validates (and potentially refreshes) the token.
  if (isDev) console.log('[Socket] initializeSocket - creating anonymous socket');
  socket = createSocket();
  return socket;
}

function handleRealtimeEvent(event: RealtimeEvent): void {
  switch (event.type) {
    case 'user.welcome':
      if (isDev) console.log('Welcome event:', event.payload);
      break;
    case 'user.email_verified':
      if (isDev) console.log('Email verified:', event.payload);
      break;
    case 'system.maintenance':
      // System maintenance warnings should always be visible
      console.warn('System maintenance:', event.payload);
      break;
    default:
      if (isDev) console.log('Unknown event type:', event.type, event.payload);
  }
}

export function getSocket(): Socket | null {
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  isAuthenticated = false;
  shouldAuthenticate = false;
  refreshAttempted = false;
}

/**
 * Upgrade socket connection when user authenticates
 * Call this after successful login
 * 
 * Instead of creating a new socket, we authenticate the existing one.
 * This preserves the listeners that were set up by useSocket hook.
 * 
 * @returns Promise that resolves when authentication is complete
 */
export function authenticateSocket(token: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!token) {
      if (isDev) console.warn('[Socket] authenticateSocket called without token');
      reject(new Error('No token provided'));
      return;
    }

    // If no socket exists, create one with the token
    if (!socket) {
      if (isDev) console.log('[Socket] No socket exists, creating new authenticated socket');
      socket = createSocket(token);
      
      let createTimeout: ReturnType<typeof setTimeout>;
      
      // Wait for authenticated event
      const onAuthenticated = () => {
        clearTimeout(createTimeout);
        socket?.off('authenticated', onAuthenticated);
        socket?.off('authentication_error', onAuthError);
        resolve();
      };
      const onAuthError = (error: { message: string }) => {
        clearTimeout(createTimeout);
        socket?.off('authenticated', onAuthenticated);
        socket?.off('authentication_error', onAuthError);
        reject(new Error(error.message));
      };
      
      socket.once('authenticated', onAuthenticated);
      socket.once('authentication_error', onAuthError);
      
      // Timeout after 10 seconds
      createTimeout = setTimeout(() => {
        socket?.off('authenticated', onAuthenticated);
        socket?.off('authentication_error', onAuthError);
        // Don't reject - socket might still work
        resolve();
      }, 10000);
      
      return;
    }

    console.log('[Socket] Authenticating existing socket', { 
      connected: socket.connected, 
      socketId: socket.id 
    });

    // Timeout reference for cleanup
    let authTimeout: ReturnType<typeof setTimeout>;
    
    // Set up listeners for authentication result
    const onAuthenticated = () => {
      console.log('[Socket] Authentication successful');
      clearTimeout(authTimeout);
      socket?.off('authenticated', onAuthenticated);
      socket?.off('authentication_error', onAuthError);
      resolve();
    };
    const onAuthError = (error: { message: string }) => {
      console.error('[Socket] Authentication failed:', error.message);
      clearTimeout(authTimeout);
      socket?.off('authenticated', onAuthenticated);
      socket?.off('authentication_error', onAuthError);
      reject(new Error(error.message));
    };
    
    socket.once('authenticated', onAuthenticated);
    socket.once('authentication_error', onAuthError);
    
    // Timeout after 10 seconds
    authTimeout = setTimeout(() => {
      socket?.off('authenticated', onAuthenticated);
      socket?.off('authentication_error', onAuthError);
      console.warn('[Socket] Authentication timeout - continuing anyway');
      // Don't reject - socket might still work
      resolve();
    }, 10000);

    // If socket is connected, just send authenticate event
    if (socket.connected) {
      console.log('[Socket] Socket connected, sending authenticate event');
      socket.emit('authenticate', { token });
    } else {
      // Socket not connected - wait for connection then authenticate
      console.log('[Socket] Socket not connected, waiting for connection...');
      
      // Update auth for reconnection
      socket.auth = { ...socket.auth, token };
      
      const onConnect = () => {
        console.log('[Socket] Socket connected, now sending authenticate event');
        socket?.off('connect', onConnect);
        socket?.emit('authenticate', { token });
      };
      
      socket.once('connect', onConnect);
      
      // If socket is not trying to connect, force it
      if (!socket.active) {
        console.log('[Socket] Socket not active, forcing connect');
        socket.connect();
      }
    }
  });
}
